import { useState, useEffect, useRef } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { getRoleConfig } from '../../utils/roleUtils'
import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  MessageSquare,
  MoreHorizontal,
  Inbox,
  Calendar,
  Film,
  HardDrive,
  Palette,
  StickyNote,
  Clapperboard,
  Wrench,
  Wallet,
  FileText,
  BookUser,
  Share2,
  Users,
  UserCheck,
  UsersRound,
  Settings,
  LogOut,
  X,
} from 'lucide-react'

const primaryNav = [
  { to: '/dashboard', label: 'Home', icon: LayoutDashboard },
  { to: '/progetti', label: 'Progetti', icon: FolderKanban },
  { to: '/tasks', label: 'Tasks', icon: CheckSquare },
  { to: '/chat', label: 'Chat', icon: MessageSquare, badge: true },
]

const secondaryNav = [
  { to: '/bacheca', label: 'Bacheca', icon: Inbox },
  { to: '/calendario', label: 'Calendario', icon: Calendar },
  { to: '/revisioni', label: 'Revisioni', icon: Film },
  { to: '/storage', label: 'Storage', icon: HardDrive },
  { to: '/moodboard', label: 'Moodboard', icon: Palette },
  { to: '/note', label: 'Note', icon: StickyNote },
  { to: '/montati', label: 'Bollettino', icon: Clapperboard },
  { to: '/tools', label: 'Tools', icon: Wrench },
]

const adminNav = [
  { to: '/finance', label: 'Finance', icon: Wallet },
  { to: '/preventivi', label: 'Preventivi', icon: FileText },
  { to: '/rubrica', label: 'Rubrica', icon: BookUser },
  { to: '/social', label: 'Social', icon: Share2 },
  { to: '/creativi', label: 'Creativi', icon: Users },
  { to: '/clienti', label: 'Clienti', icon: UserCheck },
  { to: '/team', label: 'Team', icon: UsersRound },
]

const BottomNav = () => {
  const { profile, signOut, getDisplayName } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const [sheetOpen, setSheetOpen] = useState(false)
  const channelRef = useRef(null)
  const myId = profile?.id
  const isAdmin = profile?.role === 'admin'

  const displayName = getDisplayName()
  const avatarUrl = profile?.avatar_url ? `${profile.avatar_url}?t=0` : null
  const avatarLetter = displayName?.[0]?.toUpperCase() ?? '?'

  // Determina se la route attiva è nella sezione "Altro"
  const secondaryPaths = secondaryNav.map(i => i.to)
  const adminPaths = adminNav.map(i => i.to)
  const isAltroActive = [...secondaryPaths, ...adminPaths, '/impostazioni'].some(
    p => location.pathname === p || location.pathname.startsWith(p + '/')
  )

  useEffect(() => {
    if (location.pathname === '/chat') setUnreadCount(0)
    // Chiudi lo sheet alla navigazione
    setSheetOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!myId) return
    const fetchUnread = async () => {
      const { count } = await supabase
        .from('chat_messaggi')
        .select('id', { count: 'exact', head: true })
        .eq('receiver_id', myId)
        .is('letto_at', null)
      setUnreadCount(count ?? 0)
    }
    fetchUnread()
    if (channelRef.current) supabase.removeChannel(channelRef.current)
    const ch = supabase.channel(`bottomnav-unread-${myId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messaggi', filter: `receiver_id=eq.${myId}` },
        () => { if (window.location.pathname !== '/chat') setUnreadCount(p => p + 1) })
      .subscribe()
    channelRef.current = ch
    return () => { supabase.removeChannel(ch) }
  }, [myId])

  const handleNavLink = (to) => {
    navigate(to)
  }

  return (
    <>
      {/* Sheet backdrop */}
      {sheetOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSheetOpen(false)}
        />
      )}

      {/* Bottom Sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-gray-900 border-t border-gray-800 rounded-t-2xl shadow-2xl transition-transform duration-300 ease-out ${
          sheetOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-700 rounded-full" />
        </div>

        {/* Sheet header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <p className="text-sm font-semibold text-white">Navigazione</p>
          <button
            onClick={() => setSheetOpen(false)}
            className="text-gray-500 hover:text-white transition-colors p-1"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4 max-h-[75vh] overflow-y-auto pb-6">

          {/* Voci secondarie — griglia 2 col */}
          <div className="grid grid-cols-2 gap-2">
            {secondaryNav.map(({ to, label, icon: Icon }) => {
              const isActive = location.pathname === to || location.pathname.startsWith(to + '/')
              return (
                <button
                  key={to}
                  onClick={() => handleNavLink(to)}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-colors text-left ${
                    isActive
                      ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-transparent'
                  }`}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  {label}
                </button>
              )
            })}
          </div>

          {/* Sezione Admin */}
          {isAdmin && (
            <div>
              <p className="text-[10px] text-gray-600 uppercase tracking-widest font-semibold px-1 mb-2">
                Gestione
              </p>
              <div className="grid grid-cols-2 gap-2">
                {adminNav.map(({ to, label, icon: Icon }) => {
                  const isActive = location.pathname === to || location.pathname.startsWith(to + '/')
                  return (
                    <button
                      key={to}
                      onClick={() => handleNavLink(to)}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-colors text-left ${
                        isActive
                          ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-transparent'
                      }`}
                    >
                      <Icon size={18} className="flex-shrink-0" />
                      {label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Profilo + Impostazioni + Logout */}
          <div className="border-t border-gray-800 pt-4 space-y-2">
            <button
              onClick={() => handleNavLink('/impostazioni')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                location.pathname === '/impostazioni'
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white text-sm font-bold overflow-hidden flex-shrink-0">
                {avatarUrl
                  ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
                  : avatarLetter
                }
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium truncate">{displayName}</p>
                <p className={`text-xs font-medium ${getRoleConfig(profile?.role).className}`}>{getRoleConfig(profile?.role).label}</p>
              </div>
              <Settings size={15} className="text-gray-500 flex-shrink-0" />
            </button>

            <button
              onClick={() => { setSheetOpen(false); signOut() }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <LogOut size={18} />
              <span className="text-sm font-medium">Esci</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Tab Bar */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 flex items-center justify-around px-2 py-2 z-30 lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {primaryNav.map(({ to, label, icon: Icon, badge }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/dashboard'}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                isActive ? 'text-violet-400' : 'text-gray-500'
              }`
            }
          >
            <div className="relative">
              <Icon size={20} />
              {badge && unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <span className="text-xs font-medium">{label}</span>
          </NavLink>
        ))}

        {/* Altro */}
        <button
          onClick={() => setSheetOpen(v => !v)}
          className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
            isAltroActive || sheetOpen ? 'text-violet-400' : 'text-gray-500'
          }`}
        >
          <div className="relative">
            <MoreHorizontal size={20} />
            {isAltroActive && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-violet-500 rounded-full" />
            )}
          </div>
          <span className="text-xs font-medium">Altro</span>
        </button>
      </div>
    </>
  )
}

export default BottomNav
